import React, { Component } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Navigate } from 'react-router';
import { makeLinks, withRouter } from 'utils/common';
import { partial } from 'underscore';
import axios from 'axios';
import moment from 'moment';
import BikeDetails from './Pages/BikeDetails';
import Confirmation from './Pages/Confirmation';
import endpoints from 'utils/endpoints';
import Payment from './Pages/Payment';
import PersonalData from './Pages/PersonalData';
import PaymentSuccess from 'components/PaymentSuccess';
import PaymentError from 'components/PaymentError';
import Head from 'components/Head';
import i18n from 'utils/translate';
import { PAYMENT_RESPONSE } from 'utils/constants';
import { isAustrianIBAN } from 'utils/common';

import { DATA_STORAGE_KEY, PAYMENT_PAYMENT_SLIP, PAYMENT_SEPA, PAYMENT_TYPE_ANNUAL } from './constants';
import ErrorMessage from 'components/ErrorMessage';
import { toast } from 'react-toastify';
import { calculateEndDate, paymentDetails } from './utils';
import SummaryBox from 'components/SummaryBox';

const steps = [
  { text: 'Versicherung', url: '', icon: 'insurance' },
  { text: 'Personendaten', url: 'person', icon: 'identification' },
  { text: 'Zahlung', url: 'payment', icon: 'wallet' },
  { text: 'Abschluss', url: 'confirmation', icon: 'badge' }
];

class BikeInsuranceContainer extends Component {
  constructor(props) {
    super(props);

    const { location } = props;

    this.params = {};
    this.ajaxLoading = false;
    this.blockPayment = false;

    this.state = {
      loading: false,
      apiErrorMessages: [],
      restored: false
    };

    if (location.pathname.indexOf('/confirm') === -1) {
      sessionStorage.clear();
    }

    this.initializeData();

    this.steps = steps.map(step => ({
      ...step,
      path: `${step.path}`
    }));

    this.urls = {
      bike: '/',
      personal: '/person',
      payment: '/payment',
      confirm: '/confirmation',
      success: '/success',
      error: '/error'
    };
  }

  initializeData = () => {
    const start_date = moment().add(1, 'day');
    const end_date = moment().add(1, 'year');

    this.data = {
      bike: {
        ebike: false,
        start_date: start_date.format('DD.MM.YYYY'),
        end_date: end_date.format('DD.MM.YYYY')
      },
      personal: {},
      payment: {},
      documents: {}
    };
  };

  componentDidMount() {
    // Restore data (if any) from temporary storage
    const frozen = sessionStorage.getItem(DATA_STORAGE_KEY);
    if (frozen === null) {
      this.setState({
        restored: true
      });
      return;
    }
    try {
      const data = JSON.parse(frozen);
      this.data = data;

      this.setState({ ...data, restored: true });
    } catch (e) {
      // Nothing to do here: this was an optional op
    }
  }

  updateData = (key, value) => {
    const { bike } = this.state;

    if (key === 'personal') {
      const { geburtsdatum } = value;
      this.setState({ bike: { ...bike, geburtsdatum } });
      this.data.bike.geburtsdatum = geburtsdatum;
    }

    this.data[key] = value;
    this.setState({ [key]: value });
  };

  makeQuoteModel({ ebike, markeModell, baujahr, kaufpreis, rahmennummer, versicherungsbeginn }) {
    const { anrede, vorname, nachname, strasse, nr, plz, ort, email, telefon } = this.data.personal;

    const { geburtsdatum } = this.data.bike;

    const { promotion, brokerNumber } = this.data.payment;

    if (versicherungsbeginn) {
      versicherungsbeginn = versicherungsbeginn.format('DD.MM.YYYY');
    }

    if (kaufpreis) {
      kaufpreis = +kaufpreis;
    }

    if (baujahr) {
      baujahr = baujahr.format('YYYY');
    }

    return {
      ebike,
      markeModell,
      baujahr,
      kaufpreis,
      rahmennummer,
      versicherungsbeginn,
      graweInfos: promotion,
      vermittlerNr: brokerNumber,
      versicherter: {
        anrede,
        vorname,
        nachname,
        geburtsdatum: geburtsdatum ? moment(geburtsdatum).format('DD.MM.YYYY') : '01.01.2000',
        strasse,
        nr,
        plz,
        ort,
        email,
        telefon,
        land: 'AT'
      }
    };
  }

  makeOrderModel() {
    const data = this.makeQuoteModel(this.data.bike);
    const {
      payment: { type, iban, bic, zahlungsweise }
    } = this.data;

    const prefix = window.base === '/' ? '' : window.base;

    if (type === PAYMENT_SEPA) {
      data.zahlungsweise = zahlungsweise || PAYMENT_TYPE_ANNUAL;
    } else {
      data.zahlungsweise = PAYMENT_TYPE_ANNUAL;
    }

    return {
      ...data,
      bezahlung: {
        type: type === PAYMENT_SEPA ? PAYMENT_SEPA : PAYMENT_PAYMENT_SLIP,
        hobexRequestUrl: type === PAYMENT_SEPA ? undefined : `${window.location.origin}${prefix}/confirmation`,
        iban: type === PAYMENT_SEPA ? iban.replace(/\s+/g, '') : undefined,
        bic: type === PAYMENT_SEPA ? (isAustrianIBAN(iban) ? '' : bic) : undefined,
        paymentMethod: type === PAYMENT_SEPA ? 'SEPA_DIRECTDEBITMANDATE' : 'ERLAGSCHEIN'
      }
    };
  }

  calculateQuote = bikeDetails => {
    const data = this.makeQuoteModel(bikeDetails || this.data.bike);
    const {
      versicherter: { geburtsdatum }
    } = data;

    return axios({
      baseURL: GRAWE.api_root,
      url: endpoints.bike.GET_PRICE,
      method: 'post',
      data: {
        ...data,
        versicherter: { geburtsdatum }
      },
      withCredentials: true
    })
      .then(response => response.data)
      .then(({ ok, praemie: price, waehrung: currency, zahlungsweisen: paymentVariants, validierungsMeldungen }) => {
        if (!ok) {
          validierungsMeldungen.forEach(error => {
            toast.error(<ErrorMessage header={error.feld} message={error.validierungsText} />);
          });
          return { validierungsMeldungen };
        } else {
          toast.dismiss();
          return { price, currency, paymentVariants, validierungsMeldungen };
        }
      })
      .catch(() => null)
      .then(quote => (this.data.quote = quote));
  };

  resetApiErros = () => {
    this.setState({
      apiErrorMessages: []
    });
  };

  storeTemporarily() {
    // store data to be temporarily picked up by re-navigation
    const frozen = JSON.stringify(this.data);
    sessionStorage.setItem(DATA_STORAGE_KEY, frozen);
  }

  placeSepaOrder() {
    const data = this.makeOrderModel();
    const { navigate } = this.props.router;

    return axios({
      baseURL: GRAWE.api_root,
      url: endpoints.bike.SEPA,
      method: 'post',
      data
    })
      .then(resp => resp.data)
      .then(
        ({ validierungsMeldungen: errors, status }) => {
          if (status === PAYMENT_RESPONSE.ERROR) {
            const apiErrors = [{ validierungsText: `${i18n('SEPA-Zahlung fehlgeschlagen')}` }];
            this.setState({
              apiErrorMessages: apiErrors,
              status: status
            });
          } else {
            this.setState({
              status: status
            });
            navigate('/success');
          }
          this.setState({
            loading: false
          });
        },
        () => {
          this.setState({
            loading: false,
            apiErrorMessages: [{ validierungsText: `${i18n('SEPA-Zahlung fehlgeschlagen')}` }]
          });
        }
      );
  }

  placePaymentSlipOrder() {
    const data = this.makeOrderModel();
    const { navigate } = this.props.router;

    return axios({
      baseURL: GRAWE.api_root,
      url: endpoints.bike.PAYMENT_SLIP,
      method: 'post',
      data
    })
      .then(resp => resp.data)
      .then(
        ({ validierungsMeldungen: errors, status }) => {
          if (status === PAYMENT_RESPONSE.ERROR) {
            const apiErrors = [{ validierungsText: `${i18n('Erlagschein-Zahlung fehlgeschlagen')}` }];
            this.setState({
              apiErrorMessages: apiErrors,
              status: status
            });
          } else {
            this.setState({
              status: status
            });
            navigate('/success');
          }
          this.setState({
            loading: false
          });
        },
        () => {
          this.setState({
            loading: false,
            apiErrorMessages: [{ validierungsText: `${i18n('Erlagschein-Zahlung fehlgeschlagen')}` }]
          });
        }
      );
  }

  placeOrder = () => {
    const {
      payment: { type: payment_type }
    } = this.data;

    this.setState({
      loading: true
    });

    if (payment_type === PAYMENT_SEPA) {
      this.placeSepaOrder();
    } else {
      this.placePaymentSlipOrder();
    }
  };

  makeLinks(previous, current, next) {
    const { navigate, location } = this.props.router;

    return makeLinks(previous, current, next, navigate, location);
  }

  renderBikeDetailsPage = () => {
    sessionStorage.clear();

    const props = {
      calculateQuote: this.calculateQuote,
      ...this.makeLinks(null, this.urls.bike, this.urls.personal),
      onChange: partial(this.updateData, 'bike'),
      data: this.data.bike,
      startDate: this.data.bike.start_date,
      endDate: this.data.bike.end_date,
      birthdate: this.data.bike.geburtsdatum
    };
    return <BikeDetails {...props} />;
  };

  renderSummaryBox() {
    const {
      bike: { ebike, kaufpreis, versicherungsbeginn },
      quote,
      payment: { zahlungsweise, type }
    } = this.data;

    const displayPrice = quote?.price ?? 0;

    const sections = {
      ebike,
      bikePrice: kaufpreis,
      startDate: versicherungsbeginn?.format('DD.MM.YYYY'),
      endDate: calculateEndDate(versicherungsbeginn)
    };

    if (type === PAYMENT_SEPA) {
      sections.paymentType = paymentDetails(zahlungsweise, quote);
    }

    return (
      <SummaryBox
        title={i18n('Zusammenfassung')}
        sections={sections}
        finalPrice={displayPrice}
        currency={quote && quote.currency}
        bike
      />
    );
  }

  renderPersonalDetailsPage = () => {
    if (
      !this.data.bike.markeModell ||
      !this.data.bike.rahmennummer ||
      !this.data.bike.price_currency ||
      !this.data.bike.baujahr ||
      !this.data.bike.start_date
    ) {
      return <Navigate replace to={this.urls.bike} />;
    }

    const {
      bike: { kaufpreis, versicherungsbeginn, ebike },
      quote
    } = this.data;

    sessionStorage.clear();

    const props = {
      ebike: !!ebike,
      bikePrice: +kaufpreis,
      startDate: versicherungsbeginn,
      quote,
      ...this.makeLinks(this.urls.bike, this.urls.personal, this.urls.payment),
      onChange: partial(this.updateData, 'personal'),
      data: this.data.personal,
      birthdate: this.data.bike.geburtsdatum,
      renderSummaryBox: this.renderSummaryBox()
    };

    return <PersonalData {...props} />;
  };

  renderPaymentPage = () => {
    if (!Object.keys(this.data.personal).length) {
      return <Navigate replace to={this.urls.personal} />;
    }

    const {
      bike: { ebike, kaufpreis, versicherungsbeginn },
      quote
    } = this.data;

    const props = {
      ebike,
      bikePrice: +kaufpreis,
      startDate: versicherungsbeginn?.format('DD.MM.YYYY'),
      quote,
      calculateQuote: this.calculateQuote,
      ...this.makeLinks(this.urls.personal, this.urls.payment, this.urls.confirm),
      onChange: partial(this.updateData, 'payment'),
      data: this.data.payment,
      renderSummaryBox: this.renderSummaryBox()
    };

    return <Payment {...props} />;
  };

  renderConfirmPage = () => {
    let errorMessages = [];

    if (!Object.keys(this.data.personal) || !Object.keys(this.data.payment).length) {
      return <Navigate replace to={this.urls.personal} />;
    }

    const {
      bike,
      personal,
      payment: { type: payment_type, ...payment }
    } = this.data;

    if (payment_type !== PAYMENT_SEPA) {
      delete payment.zahlungsweise;
    }

    const { loading } = this.state;

    const props = {
      ...bike,
      ...personal,
      ...payment,
      payment_type,
      loading,
      paymentPending: this.ajaxLoading,
      onRender: this.resetApiErros,
      onConfirm: this.placeOrder,
      apiErrorMessages: errorMessages.length ? errorMessages : this.state.apiErrorMessages,
      ...this.makeLinks(this.urls.payment, this.urls.confirm, null)
    };

    return <Confirmation {...props} />;
  };

  renderSuccessPage = () => {
    const { status } = this.state;

    if (!Object.keys(this.data.personal).length) {
      return <Navigate replace to={this.urls.bike} />;
    }

    return (
      <PaymentSuccess
        status={status}
        onSuccess={() => {
          this.blockPayment = true;
        }}
        resetData={this.initializeData}
      />
    );
  };

  render() {
    const prefix = window.base === '/' ? '' : window.base;
    const { restored } = this.state;

    if (!restored) {
      return null;
    }

    return (
      <div>
        <Head
          title={i18n('Fahrraddiebstahl-Versicherung')}
          steps={this.steps}
          showSteps={window.location.pathname !== `${prefix}/success` && window.location.pathname !== `${prefix}/error`}
        />
        <Routes>
          <Route path={this.urls.bike} Component={this.renderBikeDetailsPage} />
          <Route path={this.urls.personal} Component={this.renderPersonalDetailsPage} />
          <Route path={this.urls.payment} Component={this.renderPaymentPage} />
          <Route path={this.urls.confirm} Component={this.renderConfirmPage} />
          <Route path={this.urls.success} Component={this.renderSuccessPage} />
          <Route path={this.urls.error} element={<PaymentError />} />
          <Route path="/" element={<Navigate replace to={this.urls.personal} />} />
        </Routes>
      </div>
    );
  }
}

export default withRouter(BikeInsuranceContainer);
